import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { resolveOrgIcon } from '../utils/orgBranding';
import './StageDoorOverlay.css';

/**
 * "Setting the stage" - the full-screen scene shown after an explicit
 * sign-in, while the profile, organisation and dashboard chunk actually
 * load behind it. It is loading choreography, not a timed splash: it holds
 * for a minimum beat so it never flashes, then leaves the moment the data
 * is ready. A hard lifetime cap guarantees it can never trap the app.
 *
 * Visually it continues the login pane's limelight: the beam that lit the
 * word "Rehearsals" now sweeps on and finds the user's own organisation.
 */

/** Exit animation length - keep in step with the .sdo-leaving transition. */
const EXIT_MS = 600;
/** The overlay dismisses no matter what after this long. */
const MAX_LIFETIME_MS = 8000;

/** Deterministic dust motes: [left %, size px, duration s, delay s]. */
const MOTES: Array<[number, number, number, number]> = [
  [40, 3, 11, 0],
  [46, 2, 14, -3],
  [52, 4, 9, -6],
  [57, 2, 13, -1],
  [62, 3, 12, -8],
  [44, 2, 16, -10],
  [55, 3, 10, -4],
  [49, 2, 15, -7],
];

const StageDoorOverlay: React.FC = () => {
  const { profile, dismissStageIntro } = useAuth();
  const organizationId = profile?.organizationId;
  const { data: organization, isSuccess, isError } =
    useOrganizationData(organizationId);

  const reduceMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const [minHoldDone, setMinHoldDone] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Long enough for the sweep-and-reveal to land; shorter when the scene is
  // static under Reduce Motion.
  useEffect(() => {
    const t = window.setTimeout(
      () => setMinHoldDone(true),
      reduceMotion ? 1200 : 2400
    );
    return () => window.clearTimeout(t);
  }, [reduceMotion]);

  // Ready = profile arrived, and the organisation query settled (either way -
  // a failed org fetch must not hold the door).
  const organizationSettled = !organizationId || isSuccess || isError;
  const ready = !!profile && organizationSettled;

  useEffect(() => {
    if (!leaving && minHoldDone && ready) {
      setLeaving(true);
    }
  }, [leaving, minHoldDone, ready]);

  useEffect(() => {
    if (!leaving) return;
    const t = window.setTimeout(dismissStageIntro, EXIT_MS);
    return () => window.clearTimeout(t);
  }, [leaving, dismissStageIntro]);

  // The hard cap: even if a query never settles, the show goes on.
  useEffect(() => {
    const t = window.setTimeout(() => setLeaving(true), MAX_LIFETIME_MS);
    return () => window.clearTimeout(t);
  }, []);

  const organizationName = organization?.name?.trim();
  // The square app icon, not the main logo - the main logo can be any shape
  // and reads badly clipped to a circle. No icon means no circle; the name
  // alone in the beam is the fallback.
  //
  // Always the dark artwork: the curtain scene is dark whatever theme the
  // app is in, so a mark drawn in dark ink would vanish into the beam even
  // for someone signing in in light mode.
  const organizationLogo = resolveOrgIcon(organization, true);

  return (
    <div
      className={`stage-door${leaving ? ' sdo-leaving' : ''}`}
      role="status"
    >
      <p className="sr-only">Signing you in</p>
      <div className="sdo-scene" aria-hidden="true">
        <div className="sdo-haze" />
        <div className="sdo-curtain sdo-curtain-left" />
        <div className="sdo-curtain sdo-curtain-right" />

        <div className="sdo-beam-wrap">
          <div className="sdo-lamp" />
          <div className="sdo-beam sdo-beam-soft" />
          <div className="sdo-beam sdo-beam-core" />
          {MOTES.map(([left, size, duration, delay], i) => (
            <span
              key={i}
              className="sdo-mote"
              style={{
                left: `${left}%`,
                width: size,
                height: size,
                animationDuration: `${duration}s`,
                animationDelay: `${delay}s`,
              }}
            />
          ))}
        </div>
        <div className="sdo-pool" />

        {/* Keyed so the reveal replays if the name arrives mid-scene */}
        <div className="sdo-copy" key={organizationName ?? 'pending'}>
          {organizationLogo && (
            <img src={organizationLogo} alt="" className="sdo-logo" />
          )}
          {organizationName ? (
            <>
              <p className="sdo-eyebrow">Setting the stage for</p>
              <h1 className="sdo-title">{organizationName}</h1>
            </>
          ) : (
            <h1 className="sdo-title">Setting the stage</h1>
          )}
          <div className="sdo-status">
            <span className="sdo-spinner" />
            <span>Building your creative space</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageDoorOverlay;
